import { useState, useCallback } from 'react';

interface UIState {
  hasUnsavedChanges: boolean;
  showUnsavedModal: boolean;
  pendingNavigation: (() => void) | null;
}

export const useUnsavedChanges = () => {
  const [uiState, setUIState] = useState<UIState>({
    hasUnsavedChanges: false,
    showUnsavedModal: false,
    pendingNavigation: null,
  });

  const setHasUnsavedChanges = useCallback((hasChanges: boolean) => {
    setUIState((prev) => {
      if (prev.hasUnsavedChanges === hasChanges) {
        return prev;
      }
      return { ...prev, hasUnsavedChanges: hasChanges };
    });
  }, []);

  const clearUnsavedChanges = useCallback(() => {
    setUIState((prev) => ({ ...prev, hasUnsavedChanges: false }));
  }, []);

  const showModal = useCallback((navigation: () => void) => {
    setUIState((prev) => ({
      ...prev,
      pendingNavigation: navigation,
      showUnsavedModal: true,
    }));
  }, []);

  const hideModal = useCallback(() => {
    setUIState((prev) => ({
      ...prev,
      showUnsavedModal: false,
      pendingNavigation: null,
    }));
  }, []);

  const proceedWithNavigation = useCallback(() => {
    setUIState((prev) => {
      const navigationToCall = prev.pendingNavigation;

      // Call navigation before clearing state
      if (navigationToCall) {
        navigationToCall();
      }

      return {
        ...prev,
        hasUnsavedChanges: false,
        showUnsavedModal: false,
        pendingNavigation: null,
      };
    });
  }, []);

  return {
    uiState,
    setHasUnsavedChanges,
    clearUnsavedChanges,
    showModal,
    hideModal,
    proceedWithNavigation,
  };
};
